import { useState } from 'react'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultTitle,
  DefaultSubtitle,
  ReviewCardContainer,
} from 'src/components/application/defaultComponents'
import ApplicationPrebuiltFooter from 'src/components/application/PrebuiltBodyFooter'
import { useApplication } from 'src/context/application'
import { useNavigate, createSearchParams, useLocation } from 'react-router-dom'
import Button from 'src/components/basic/Button'
import { IoMdCheckmark } from 'react-icons/io'
import ApplicationData, { ReviewData } from 'src/dtos/applicationData'
import { IoIosCheckmarkCircle } from 'react-icons/io'
import StandardInput from 'src/components/basic/StandardInput'
import FilePicker from 'src/components/basic/FilePicker'
import { uploadFiles } from 'src/services/apiClient'
import { judgeFullName, judgeContactFullName } from 'src/utils/newApplications'
import {FILE_SIZE_LABEL} from "../../../../utils/constants";

const Judge = ({screenshotMode = false}) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { application, saveApplication } = useApplication()
  const [review, setReview] = useState<ReviewData>(application?.judgeApplicationData?.basicSection?.review ?? {})
  const [showNotesInput, setShowNotesInput] = useState(!!application?.judgeApplicationData?.basicSection?.review?.note)
  const [showFilePicker, setShowFilePicker] = useState(!!application?.judgeApplicationData?.basicSection?.review?.files)

  const handleEditClick = () => {
    navigate({
      pathname: '/judge/basic/main',
      search: createSearchParams({ review: 'basic' }).toString()
    })
  }

  const updatedApplication = (): ApplicationData => ({
    ...application,
    stoppedAt: pathname,
    judgeApplicationData: {
      ...application?.judgeApplicationData,
      basicSection: {
        ...application?.judgeApplicationData?.basicSection,
        review
      }
    }
  })

  const handleSaveApplication = async () => {
    try {
      await saveApplication(updatedApplication())
    } catch (_) { }
  }

  const produceApplicationData = (key: keyof ReviewData, value: any) => ({
    ...application,
    stoppedAt: pathname,
    judgeApplicationData: {
      ...application?.judgeApplicationData,
      basicSection: {
        ...application?.judgeApplicationData?.basicSection,
        review: {
          ...application?.judgeApplicationData?.basicSection?.review,
          [key]: value
        }
      }
    }
  })

  const handleRemoveNote = async () => {
    const removedNoteApplication = produceApplicationData('note', undefined)
    setReview(prev => ({ ...prev, note: undefined }))
    setShowNotesInput(false)
    await saveApplication(removedNoteApplication)
  }

  const handleUploadFiles = async (files: File[]) => {
    if(files.length > 0) {
      const uploadedFiles = await uploadFiles(files)

      const updatedFiles = [...(review?.files ?? []), ...(uploadedFiles ?? [])]
  
      const applicationFileAttached = produceApplicationData('files', updatedFiles)
      
      setReview(prev => ({ ...prev, files: updatedFiles }))
      await saveApplication(applicationFileAttached)
    } else {
      setReview(prev => ({ ...prev, files: undefined }))
      const applicationFileAttached = produceApplicationData('files', undefined)
      await saveApplication(applicationFileAttached)
    }
  }

  const basicSection = application?.judgeApplicationData?.basicSection

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent width='80%'>
        <DefaultTitle>Almost done! Please review your basic information before you sign.</DefaultTitle>
        <DefaultSubtitle>If any answers are innacurate or incomplete you can use the edit button to change them.</DefaultSubtitle>
        <ReviewCardContainer id='judgeData'>
          <header>
            <div>
              <h4>Judge</h4>
              {!screenshotMode && (
                <h6>
                  <IoMdCheckmark />
                  Complete
                </h6>
              )}
            </div>
            {!screenshotMode && (
              <div>
                <Button style={{ padding: '8px 46px' }} onClick={handleEditClick}>Edit Section</Button>
              </div>
            )}
          </header>
          <main className='firm'>
            <section>
              <h5>BASIC INFORMATION</h5>
              <article>
                <strong>Full Name of Judge</strong>
                <span>{judgeFullName(basicSection)}</span>
              </article>
              <article>
                <strong>Contact Info</strong>
                <span>{judgeContactFullName(basicSection)}</span>
                {basicSection?.contactInfo?.isSameAsJudge === true ? (
                  <span style={{ flexDirection: 'row', alignItems: 'center' }}><IoIosCheckmarkCircle /> Same as Judge listed above</span>
                ) : null}
                <span>{basicSection?.contactInfo?.phoneNumber ?? 'N/A'}</span>
                <span>{basicSection?.contactInfo?.emailAddress ?? 'N/A'}</span>
                {!!basicSection?.contactInfo?.faxNumber ? (
                  <span>{basicSection?.contactInfo?.faxNumber ?? 'N/A'}</span>
                ) : null}
              </article>
              <article>
                <strong>Principal office address</strong>
                <span>{basicSection?.principalOfficeAddress?.formattedAddress ?? 'N/A'}</span>
                <span>{basicSection?.principalOfficeAddress?.suiteNumber ?? 'N/A'}</span>
              </article>
              {basicSection?.hasMailingAddress === true ? (
                <article>
                  <strong>Principal Mailing Address</strong>
                  <span>{basicSection?.mailingAddress?.formattedAddress ?? 'N/A'}</span>
                  <span>{basicSection?.mailingAddress?.suiteNumber ?? 'N/A'}</span>
                </article>
              ) : null}
              {basicSection?.hasBillingAddress === true ? (
                <article>
                  <strong>Billing Address</strong>
                  <span>{basicSection?.billingAddress?.formattedAddress ?? 'N/A'}</span>
                  <span>{basicSection?.billingAddress?.suiteNumber ?? 'N/A'}</span>
                </article>
              ) : null}
              <article>
                <strong>County of the court over which the applicant presides</strong>
                <span>{basicSection?.presidingCounty ?? 'N/A'}</span>
              </article>
              <article>
                <strong>Court over which applicant presides</strong>
                <span>{basicSection?.residingCourt ?? 'N/A'}</span>
              </article>
              <article>
                <strong>Number of years you have presided over this court</strong>
                <span>{basicSection?.yearsPresidingCourt ?? 'N/A'}</span>
              </article>
              <article>
                <strong>Number of years of overall judicial experience</strong>
                <span>{basicSection?.yearsOverallExperience ?? 'N/A'}</span>
              </article>
              <article>
                <strong>Bar Card Number</strong>
                <span>{basicSection?.barCardNumber ?? 'N/A'}</span>
              </article>
              <article>
                <strong>Date admitted to Texas Bar</strong>
                <span>{basicSection?.dateAdmittedTexasBar ?? 'N/A'}</span>
              </article>
            </section>
          </main>
          {!screenshotMode && (
            <footer>
              {showNotesInput ? (
                <div className="review-footer-note-container">
                  <StandardInput
                    label=""
                    rows={15}
                    value={review?.note ?? ''}
                    onChange={e => setReview(prev => ({ ...prev, note: e.target.value }))}
                  />
                  <div className="review-footer-note-buttons">
                    {!!basicSection?.review?.note ? (
                      <Button
                        variant="error"
                        onClick={handleRemoveNote}
                      >
                        Remove note
                      </Button>
                    ) : (
                      <>
                        <Button
                          className="review-button-small-width"
                          onClick={handleSaveApplication}
                        >
                          Save details
                        </Button>
                        <Button
                          variant="error"
                          className="review-button-small-width"
                          onClick={() => {
                            setReview(prev => ({ ...prev, note: undefined }))
                            setShowNotesInput(false)
                          }}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="review-footer-button-container">
                  <Button
                    variant="secondary"
                    onClick={() => setShowNotesInput(true)}
                  >
                    Add a note
                  </Button>
                </div>
              )}
              {showFilePicker ? (
                <div className='review-footer-filepicker-container'>
                  <FilePicker 
                    title={FILE_SIZE_LABEL}
                    onFilesSelect={handleUploadFiles}
                    selectedFiles={review?.files ?? []}
                    accept='.doc,.docx,application/msword, application/pdf'
                    limit={100}
                  />
                </div>
              ) : (
                <div className="review-footer-button-container">
                  <Button
                    variant="secondary"
                    onClick={() => setShowFilePicker(true)}
                  >
                    Attach a document
                  </Button>
                </div>
              )}
            </footer>
          )}
        </ReviewCardContainer>
      </ApplicationBodyContent>
      <ApplicationPrebuiltFooter
        goBackRoute="/judge/quote/details"
        nextRoute="/judge/review/insurances"
        updatedApplication={updatedApplication()}
      />
    </ApplicationBodyContainer>
  )
}

export default Judge